import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  listMockups, createMockup, getMockup, regenerateMockup, refineMockup, deleteMockup,
  uploadMockupAsset, generateMockupHeroImage, mockupAssetUrl,
  stockPhotoSearch, importStockPhoto, analyzeProspectWebsite,
  emailMockupToProspect, getClients,
  mockupPreviewUrl, mockupHtmlUrl, mockupPdfUrl, mockupPngUrl, API_BASE,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Loader2, Sparkles, Copy, ExternalLink, FileDown, ImageDown, FileCode,
  RefreshCcw, Trash2, Wand2, ArrowLeft, ImagePlus, X as XIcon,
  Search, ImageIcon, UserCircle, Globe, CheckCircle2, Mail, Send, AlertTriangle,
} from "lucide-react";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 3 * 60 * 1000; // 3 minutes

const EMPTY_FORM = {
  business_name: "",
  industry: "",
  service_area: "",
  phone: "",
  primary_color: "",
  key_services: "",
  primary_cta: "",
  tagline: "",
  additional_instructions: "",
  hero_image_url: "",
  logo_url: "",
  website_url: "",
  client_id: "",
  client_company: "",
  client_name: "",
};

export default function MockupBuilderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get("client") || "";
  const [clients, setClients] = useState([]);
  const [mockups, setMockups] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeMockup, setActiveMockup] = useState(null); // full doc with html
  const [regenerating, setRegenerating] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const pollTimerRef = useRef(null);
  const pollStartedAtRef = useRef(null);

  const fetchList = async () => {
    try {
      const r = await listMockups(clientFilter ? { client_id: clientFilter } : {});
      setMockups(r.data || []);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchClients = async () => {
    try {
      const r = await getClients();
      setClients(r.data || []);
    } catch (_) { /* rep may have empty list, non-fatal */ }
  };

  useEffect(() => {
    fetchList();
    fetchClients();
  }, [clientFilter]);

  // If the URL says ?client=X, pre-populate the new-mockup form with that
  // client's info and business_name/industry/etc so the rep doesn't retype.
  useEffect(() => {
    if (!clientFilter || clients.length === 0) return;
    const c = clients.find((x) => x.id === clientFilter);
    if (!c) return;
    setForm((prev) => ({
      ...prev,
      client_id: c.id,
      client_company: c.company || "",
      client_name: c.name || "",
      business_name: prev.business_name || c.company || c.name || "",
      industry: prev.industry || c.industry || "",
      phone: prev.phone || c.phone || "",
    }));
  }, [clientFilter, clients]);

  // Deep link: /mockups?id=... auto-opens a saved mockup
  useEffect(() => {
    const id = searchParams.get("id");
    if (id && id !== activeId) {
      openMockup(id);
    }
  }, [searchParams]);

  useEffect(() => () => stopPolling(), []);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollStartedAtRef.current = null;
  };

  const startPolling = (id) => {
    stopPolling();
    pollStartedAtRef.current = Date.now();
    const tick = async () => {
      try {
        const r = await getMockup(id);
        setActiveMockup(r.data);
        // Refresh list to update its status chip too
        setMockups((prev) => prev.map((m) => (m.id === id ? { ...m, ...r.data, html: undefined } : m)));
        if (r.data.status === "ready") {
          stopPolling();
          setRegenerating(false);
          if (r.data.error) {
            toast.error(r.data.error);
          } else {
            toast.success("Mockup ready");
          }
          return;
        }
        if (r.data.status === "failed") {
          stopPolling();
          setRegenerating(false);
          toast.error(r.data.error || "Mockup generation failed");
          return;
        }
      } catch (e) {
        // Transient — keep polling
      }
      if (Date.now() - (pollStartedAtRef.current || 0) > POLL_MAX_MS) {
        stopPolling();
        toast.error("Mockup is taking longer than expected — refresh the page in a moment");
        return;
      }
      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
  };

  const openMockup = async (id) => {
    setActiveId(id);
    setActiveMockup(null);
    try {
      const r = await getMockup(id);
      setActiveMockup(r.data);
      if (r.data.status === "running") startPolling(id);
    } catch (e) {
      toast.error("Could not load mockup");
      setActiveId(null);
    }
  };

  const closeMockup = () => {
    stopPolling();
    setActiveId(null);
    setActiveMockup(null);
    const np = new URLSearchParams(searchParams);
    np.delete("id");
    setSearchParams(np, { replace: true });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    // Hero image is REQUIRED — mockups without a real photograph consistently
    // flop in sales calls. Rep must pick one of: Upload / AI Generate / Stock
    // Search / Paste URL. If they don't, offer to auto-generate one for them
    // via Nano Banana (fast — ~10-20s) before submitting.
    if (!form.hero_image_url) {
      const wantsAi = window.confirm(
        "A hero image is required — mockups without one don't sell as well.\n\n" +
        "Would you like me to AI-generate one right now based on the business info you've entered? " +
        "(Click OK to auto-generate — takes ~15 seconds.\n" +
        "Click Cancel to go back and Upload / Stock-search a photo yourself.)"
      );
      if (!wantsAi) {
        toast.warning("Add a hero photo (Upload, AI Generate, Stock Search, or paste URL) then click Generate again.");
        return;
      }
      // Auto-generate the hero, then continue with submit
      try {
        toast.info("Drawing a hero photo with Nano Banana… ~15s");
        const r = await generateMockupHeroImage({
          business_name: form.business_name,
          industry: form.industry,
          service_area: form.service_area,
          key_services: form.key_services,
        });
        // Update the form state so the preview thumbnail shows AND the submit
        // below uses the fresh URL.
        setForm((prev) => ({ ...prev, hero_image_url: r.data.url }));
        // Use the local URL below since setForm is async
        form.hero_image_url = r.data.url;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Auto-hero-generation failed — please pick an image manually");
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await createMockup(form);
      toast.success("Kicking off Claude — 60–90s");
      setActiveId(r.data.id);
      setActiveMockup(r.data);
      startPolling(r.data.id);
      // Prepend to list
      setMockups((prev) => [r.data, ...prev]);
      // Persist deep link in URL
      const np = new URLSearchParams(searchParams);
      np.set("id", r.data.id);
      setSearchParams(np, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create mockup");
    } finally {
      setSubmitting(false);
    }
  };

  const doRegenerate = async () => {
    if (!activeMockup) return;
    setRegenerating(true);
    try {
      // Send the CURRENT active mockup form values so the rep can edit + regen
      const r = await regenerateMockup(activeMockup.id, {
        business_name: activeMockup.business_name,
        industry: activeMockup.industry,
        service_area: activeMockup.service_area,
        phone: activeMockup.phone,
        primary_color: activeMockup.primary_color,
        key_services: activeMockup.key_services,
        primary_cta: activeMockup.primary_cta,
        tagline: activeMockup.tagline,
        additional_instructions: activeMockup.additional_instructions,
        hero_image_url: activeMockup.hero_image_url || "",
        logo_url: activeMockup.logo_url || "",
        website_url: activeMockup.website_url || "",
        client_id: activeMockup.client_id || "",
      });
      setActiveMockup(r.data);
      startPolling(r.data.id);
      toast.success("Regenerating — 60–90s");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const doRefine = async (instruction) => {
    if (!activeMockup) return;
    const inst = (instruction || "").trim();
    if (!inst) { toast.error("Type what you want changed first"); return; }
    setRegenerating(true);
    try {
      const r = await refineMockup(activeMockup.id, inst);
      setActiveMockup(r.data);
      // Backgrounded — poll until status flips from "refining" back to "ready"
      startPolling(r.data.id);
      toast.success("Refining — 30–60s");
    } catch (err) {
      setRegenerating(false);
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((e) => e?.msg || "").filter(Boolean).join("; ")
        : (err?.message || "Refine failed");
      toast.error(msg);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this mockup? This cannot be undone.")) return;
    try {
      await deleteMockup(id);
      toast.success("Mockup deleted");
      setMockups((prev) => prev.filter((m) => m.id !== id));
      if (id === activeId) closeMockup();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const shareableUrl = useMemo(
    () => (activeId ? `${API_BASE}${mockupPreviewUrl(activeId).replace(API_BASE, "")}` : ""),
    [activeId]
  );

  const copyLink = async () => {
    if (!activeId) return;
    try {
      await navigator.clipboard.writeText(shareableUrl);
      toast.success("Public link copied to clipboard");
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = shareableUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Public link copied"); } catch (_) { toast.error("Could not copy"); }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="space-y-6" data-testid="mockup-builder-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-gradient-to-br from-fuchsia-600 to-indigo-600 p-2">
              <Wand2 className="h-5 w-5 text-white" />
            </div>
            <h1 className="font-outfit text-3xl font-bold tracking-tight">Homepage Mockup Builder</h1>
          </div>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Show a prospect what their modernized homepage could look like — Claude Sonnet 4.5 drafts a self-contained
            hero page in about 60–90 seconds. Share the live link, download as PDF, or drop the PNG into your pitch deck.
          </p>
        </div>
        {activeId && (
          <Button variant="outline" onClick={closeMockup} data-testid="mockup-close-btn">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to list
          </Button>
        )}
      </div>

      {activeId ? (
        <MockupDetail
          mockup={activeMockup}
          shareableUrl={shareableUrl}
          onCopyLink={copyLink}
          onRegenerate={doRegenerate}
          onRefine={doRefine}
          regenerating={regenerating}
          onEditField={(patch) => setActiveMockup((m) => ({ ...m, ...patch }))}
          onDelete={() => doDelete(activeId)}
          onOpenEmail={() => setEmailDialogOpen(true)}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="border-2 border-fuchsia-200/60">
            <CardContent className="pt-5 pb-5 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-fuchsia-600" />
                <h2 className="font-semibold text-sm">New Mockup</h2>
              </div>
              <form onSubmit={submit} className="space-y-3">
                <Field label="Business Name *" required>
                  <Input
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    placeholder="M.A.E. Contracting"
                    data-testid="mockup-business-name"
                    required
                  />
                </Field>

                {/* Prospect's existing website — Claude uses this as context
                    (tone / positioning / brand cues) and the "Analyze" button
                    auto-fills tagline/logo/primary_color from OG tags + CSS. */}
                <WebsiteAnalyzerField
                  value={form.website_url}
                  onChange={(url) => setForm({ ...form, website_url: url })}
                  onSuggestions={(s) => setForm((prev) => ({
                    ...prev,
                    // Only fill fields that are still empty so the rep's
                    // typed values are never overwritten.
                    tagline: prev.tagline || s.tagline || "",
                    primary_color: prev.primary_color || s.primary_color || "",
                    logo_url: prev.logo_url || s.logo_url || "",
                    additional_instructions: prev.additional_instructions || s.description_note || "",
                  }))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Industry / Trades">
                    <Input
                      value={form.industry}
                      onChange={(e) => setForm({ ...form, industry: e.target.value })}
                      placeholder="Fencing, pole barns, driveways"
                      data-testid="mockup-industry"
                    />
                  </Field>
                  <Field label="Service Area">
                    <Input
                      value={form.service_area}
                      onChange={(e) => setForm({ ...form, service_area: e.target.value })}
                      placeholder="Nassau County, Florida"
                      data-testid="mockup-service-area"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone (shown top-right + CTA band)">
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="904-507-2632"
                      data-testid="mockup-phone"
                    />
                  </Field>
                  <Field label="Primary Brand Colour">
                    <div className="flex gap-2 items-center">
                      <Input
                        value={form.primary_color}
                        onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                        placeholder="#B4271C  or  navy"
                        data-testid="mockup-primary-color"
                      />
                      {form.primary_color && (
                        <div
                          className="h-8 w-8 rounded border shrink-0"
                          style={{ backgroundColor: form.primary_color }}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </Field>
                </div>
                <Field label="Key Services to Highlight">
                  <Input
                    value={form.key_services}
                    onChange={(e) => setForm({ ...form, key_services: e.target.value })}
                    placeholder="Pole barns, fencing, driveways, land clearing"
                    data-testid="mockup-services"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Primary CTA Copy">
                    <Input
                      value={form.primary_cta}
                      onChange={(e) => setForm({ ...form, primary_cta: e.target.value })}
                      placeholder="Get a free estimate"
                      data-testid="mockup-cta"
                    />
                  </Field>
                  <Field label="Tagline / Positioning">
                    <Input
                      value={form.tagline}
                      onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                      placeholder="Family-owned, built to last"
                      data-testid="mockup-tagline"
                    />
                  </Field>
                </div>
                <Field label="Additional Instructions (optional)">
                  <Textarea
                    rows={6}
                    value={form.additional_instructions}
                    onChange={(e) => setForm({ ...form, additional_instructions: e.target.value })}
                    placeholder="Any specific vibe, competitors to reference, must-have sections, etc. — drag the bottom-right corner to expand"
                    className="resize-y min-h-[140px]"
                    data-testid="mockup-notes"
                  />
                </Field>

                {/* Client link (optional) — pins this mockup to a Client
                    record so it shows up on that client's row and can be
                    grabbed without hunting. */}
                <ClientLinkPicker
                  clients={clients}
                  value={form.client_id}
                  onChange={(clientId, company, name) =>
                    setForm({
                      ...form,
                      client_id: clientId || "",
                      client_company: company || "",
                      client_name: name || "",
                      // If business_name is still blank, borrow the company
                      business_name: form.business_name || (company || name || ""),
                    })
                  }
                />

                {/* Hero image + Logo — hero is REQUIRED (mockups without one
                    don't sell). Rep can upload / AI-generate / stock-search /
                    paste URL. If they submit without one, the submit handler
                    prompts them to auto-generate via Nano Banana. */}
                <div className={`rounded-md border p-3 space-y-3 ${form.hero_image_url ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50/50 border-amber-300"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-zinc-800 flex items-center gap-1">
                      <ImagePlus className="h-3.5 w-3.5" /> Images
                      <span className="ml-1 text-[10px] font-normal text-zinc-600">
                        {form.hero_image_url
                          ? "· hero locked in — mockup will look great"
                          : "· hero photo is required (upload, AI-generate, or stock-search)"}
                      </span>
                    </p>
                    {!form.hero_image_url && (
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded" data-testid="mockup-hero-required-tag">
                        Required
                      </span>
                    )}
                  </div>
                  <ImagePicker
                    label={form.hero_image_url ? "Hero Photo" : "Hero Photo (required)"}
                    value={form.hero_image_url}
                    onChange={(url) => setForm({ ...form, hero_image_url: url })}
                    allowAiGenerate
                    allowStockSearch
                    aiPromptFields={{
                      business_name: form.business_name,
                      industry: form.industry,
                      service_area: form.service_area,
                      key_services: form.key_services,
                    }}
                    testidPrefix="mockup-hero"
                  />
                  <ImagePicker
                    label="Client Logo (optional but recommended)"
                    value={form.logo_url}
                    onChange={(url) => setForm({ ...form, logo_url: url })}
                    allowStockSearch
                    testidPrefix="mockup-logo"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700"
                  disabled={submitting}
                  data-testid="mockup-submit"
                >
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending to Claude…</>
                  ) : (
                    <><Sparkles className="mr-2 h-4 w-4" /> Generate Homepage Mockup</>
                  )}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Claude Sonnet 4.5 · takes ~60–90s · you can browse away and come back
                </p>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-sm">Recent Mockups</h2>
                {mockups.length > 0 && (
                  <Badge variant="outline" className="text-[10px]" data-testid="mockup-count">{mockups.length}</Badge>
                )}
              </div>
              {clientFilter && (
                <div className="flex items-center justify-between gap-2 rounded-md bg-indigo-50 border border-indigo-200 px-2.5 py-1.5 text-[11px]">
                  <span className="flex items-center gap-1 text-indigo-800">
                    <UserCircle className="h-3.5 w-3.5" />
                    Filtered to client: <b>{clients.find((c) => c.id === clientFilter)?.company || clients.find((c) => c.id === clientFilter)?.name || "…"}</b>
                  </span>
                  <button
                    type="button"
                    className="text-indigo-700 hover:text-indigo-900 text-[11px] underline"
                    onClick={() => {
                      const np = new URLSearchParams(searchParams);
                      np.delete("client");
                      setSearchParams(np, { replace: true });
                    }}
                    data-testid="clear-client-filter"
                  >
                    Clear filter
                  </button>
                </div>
              )}
              {loadingList ? (
                <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : mockups.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No mockups yet — fill out the form on the left to draft the first one.
                </div>
              ) : (
                <ScrollArea className="max-h-[540px]">
                  <div className="space-y-2">
                    {mockups.map((m) => (
                      <div
                        key={m.id}
                        className="border rounded-md p-3 hover:bg-zinc-50 cursor-pointer flex items-start gap-3"
                        onClick={() => openMockup(m.id)}
                        data-testid={`mockup-row-${m.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{m.business_name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {[m.industry, m.service_area].filter(Boolean).join(" · ") || "—"}
                          </p>
                          {m.client_company && (
                            <p className="text-[10px] mt-0.5 inline-flex items-center gap-0.5 text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">
                              <UserCircle className="h-2.5 w-2.5" />
                              {m.client_company}
                            </p>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {m.created_by_name || "—"} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                          </p>
                        </div>
                        <StatusChip status={m.status} />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 shrink-0"
                          onClick={(e) => { e.stopPropagation(); doDelete(m.id); }}
                          data-testid={`mockup-delete-${m.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              <div className="pt-2 border-t text-[11px] text-muted-foreground">
                <p>Tip: your mockups are private to you. Managers see their agency&apos;s; super admins see everything.</p>
                <p className="mt-1">
                  Looking for the branded ProspectMax deck or ROI Estimator? <Link to="/resources" className="text-primary hover:underline">Open Resources →</Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email-to-Prospect dialog — rendered at page-level so it works from
          either the detail card OR any future entry point without prop
          drilling more state around. */}
      {emailDialogOpen && activeMockup && (
        <EmailProspectDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          mockup={activeMockup}
          onSent={(payload) => {
            setActiveMockup((m) => ({
              ...m,
              last_emailed_at: new Date().toISOString(),
              last_emailed_to: payload?.to_email || m.last_emailed_to,
            }));
            setEmailDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}


function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}


function StatusChip({ status }) {
  if (status === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
        <Loader2 className="h-3 w-3 animate-spin" /> Generating
      </span>
    );
  }
  if (status === "failed") {
    return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">Failed</span>;
  }
  return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Ready</span>;
}


function MockupDetail({ mockup, shareableUrl, onCopyLink, onRegenerate, onRefine, regenerating, onEditField, onDelete, onOpenEmail }) {
  if (!mockup) {
    return (
      <div className="py-16 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isRunning = mockup.status === "running";
  const isReady = mockup.status === "ready";
  const isFailed = mockup.status === "failed";

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-3">
        {isRunning && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="pt-4 pb-4 flex items-start gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">Claude is drafting the mockup…</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Usually 60–90 seconds. This dialog will refresh automatically the moment it&apos;s ready — feel free to
                  browse away and come back.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {isFailed && (
          <Card className="border-red-200 bg-red-50/60">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-semibold text-red-800">Mockup generation failed</p>
              <p className="text-xs text-red-700 mt-1">{mockup.error || "Unknown error"}</p>
              <Button size="sm" className="mt-3" onClick={onRegenerate} disabled={regenerating} data-testid="mockup-retry">
                {regenerating ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCcw className="mr-2 h-3 w-3" />}
                Try again
              </Button>
            </CardContent>
          </Card>
        )}
        {isReady && (
          <div className="rounded-lg border overflow-hidden bg-white" data-testid="mockup-preview-frame">
            <div className="flex items-center justify-between px-3 py-2 border-b bg-zinc-50 text-xs">
              <span className="text-muted-foreground truncate">Preview · {mockup.business_name}</span>
              <a
                href={shareableUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Open in new tab <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <iframe
              key={mockup.updated_at}
              src={shareableUrl}
              title="Homepage Mockup Preview"
              className="w-full h-[720px] bg-white"
            />
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Card className="border-2 border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-indigo-50">
          <CardContent className="pt-5 pb-5 space-y-2">
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-gradient-to-br from-fuchsia-600 to-indigo-600 p-1.5">
                <Mail className="h-4 w-4 text-white" />
              </div>
              <h3 className="font-semibold text-sm">Send to Prospect</h3>
            </div>
            <p className="text-[11px] text-muted-foreground">
              One-click email — includes the live link, a PNG preview embedded in the body, and your signature.
              Replies go to <b>your</b> inbox.
            </p>
            <Button
              onClick={onOpenEmail}
              disabled={!isReady}
              className="w-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700"
              data-testid="mockup-email-prospect-open"
            >
              <Send className="mr-2 h-4 w-4" /> Email this mockup
            </Button>
            {mockup.last_emailed_at && (
              <p className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                <CheckCircle2 className="inline h-3 w-3 mr-0.5" />
                Last sent to <b>{mockup.last_emailed_to}</b> · {new Date(mockup.last_emailed_at).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-5 space-y-3">
            <h3 className="font-semibold text-sm">Share & Download</h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                disabled={!isReady}
                onClick={onCopyLink}
                data-testid="mockup-copy-link"
              >
                <Copy className="mr-2 h-4 w-4" /> Copy shareable link
              </Button>
              <a
                href={shareableUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`block ${isReady ? "" : "pointer-events-none opacity-50"}`}
                data-testid="mockup-open-link"
              >
                <Button variant="outline" className="w-full justify-start" disabled={!isReady}>
                  <ExternalLink className="mr-2 h-4 w-4" /> Open live preview
                </Button>
              </a>
              <a
                href={isReady ? mockupPdfUrl(mockup.id) : "#"}
                className={`block ${isReady ? "" : "pointer-events-none opacity-50"}`}
                data-testid="mockup-download-pdf"
              >
                <Button variant="outline" className="w-full justify-start" disabled={!isReady}>
                  <FileDown className="mr-2 h-4 w-4" /> Download PDF
                </Button>
              </a>
              <a
                href={isReady ? mockupPngUrl(mockup.id) : "#"}
                className={`block ${isReady ? "" : "pointer-events-none opacity-50"}`}
                data-testid="mockup-download-png"
              >
                <Button variant="outline" className="w-full justify-start" disabled={!isReady}>
                  <ImageDown className="mr-2 h-4 w-4" /> Download PNG (for slide deck)
                </Button>
              </a>
              <a
                href={isReady ? mockupHtmlUrl(mockup.id) : "#"}
                className={`block ${isReady ? "" : "pointer-events-none opacity-50"}`}
                data-testid="mockup-download-html"
              >
                <Button variant="outline" className="w-full justify-start" disabled={!isReady}>
                  <FileCode className="mr-2 h-4 w-4" /> Download HTML file
                </Button>
              </a>
            </div>
            {isReady && (
              <div className="pt-2 border-t">
                <p className="text-[10px] uppercase text-muted-foreground tracking-wide">Public link</p>
                <p className="text-[11px] break-all mt-1 bg-zinc-50 border rounded px-2 py-1">{shareableUrl}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Anyone with the link can view — no login required.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat-style refine — the fast path for "just change this one thing" */}
        {mockup.status === "ready" && (
          <RefinePanel
            regenerating={regenerating}
            history={mockup.refine_history}
            onRefine={onRefine}
          />
        )}

        <Card>
          <CardContent className="pt-5 pb-5 space-y-3">
            <h3 className="font-semibold text-sm">Rebuild From Scratch</h3>
            <p className="text-[11px] text-muted-foreground">
              Tweak any field below then hit Regenerate to have Claude produce an entirely fresh
              draft (discards current design). For small tweaks use the Refine chat above instead.
            </p>
            <div className="space-y-2">
              <Input
                value={mockup.business_name || ""}
                onChange={(e) => onEditField({ business_name: e.target.value })}
                placeholder="Business name"
                data-testid="mockup-edit-name"
              />
              <Input
                value={mockup.industry || ""}
                onChange={(e) => onEditField({ industry: e.target.value })}
                placeholder="Industry"
              />
              <Input
                value={mockup.service_area || ""}
                onChange={(e) => onEditField({ service_area: e.target.value })}
                placeholder="Service area"
              />
              <Input
                value={mockup.phone || ""}
                onChange={(e) => onEditField({ phone: e.target.value })}
                placeholder="Phone"
              />
              <Input
                value={mockup.primary_color || ""}
                onChange={(e) => onEditField({ primary_color: e.target.value })}
                placeholder="Primary colour"
              />
              <Input
                value={mockup.key_services || ""}
                onChange={(e) => onEditField({ key_services: e.target.value })}
                placeholder="Key services"
              />
              <Input
                value={mockup.primary_cta || ""}
                onChange={(e) => onEditField({ primary_cta: e.target.value })}
                placeholder="Primary CTA"
              />
              <Input
                value={mockup.tagline || ""}
                onChange={(e) => onEditField({ tagline: e.target.value })}
                placeholder="Tagline"
              />
              <Input
                value={mockup.website_url || ""}
                onChange={(e) => onEditField({ website_url: e.target.value })}
                placeholder="Prospect's current website URL"
                data-testid="mockup-edit-website-url"
              />
              <Textarea
                rows={8}
                value={mockup.additional_instructions || ""}
                onChange={(e) => onEditField({ additional_instructions: e.target.value })}
                placeholder="Additional instructions — drag the bottom-right corner to expand. No character limit."
                className="resize-y min-h-[160px]"
                data-testid="mockup-edit-notes"
              />

              <div className="rounded-md border bg-zinc-50/50 p-2 space-y-2">
                <p className="text-[11px] font-semibold text-zinc-700 flex items-center gap-1">
                  <ImagePlus className="h-3 w-3" /> Images
                </p>
                <ImagePicker
                  label="Hero Photo"
                  value={mockup.hero_image_url || ""}
                  onChange={(url) => onEditField({ hero_image_url: url })}
                  allowAiGenerate
                  allowStockSearch
                  aiPromptFields={{
                    business_name: mockup.business_name,
                    industry: mockup.industry,
                    service_area: mockup.service_area,
                    key_services: mockup.key_services,
                  }}
                  testidPrefix="mockup-edit-hero"
                  compact
                />
                <ImagePicker
                  label="Client Logo"
                  value={mockup.logo_url || ""}
                  onChange={(url) => onEditField({ logo_url: url })}
                  allowStockSearch
                  testidPrefix="mockup-edit-logo"
                  compact
                />
              </div>
            </div>
            <Button
              onClick={onRegenerate}
              disabled={regenerating || isRunning}
              className="w-full bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700"
              data-testid="mockup-regenerate"
            >
              {regenerating || isRunning ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating…</>
              ) : (
                <><RefreshCcw className="mr-2 h-4 w-4" /> Regenerate</>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={onDelete}
              data-testid="mockup-delete"
            >
              <Trash2 className="mr-2 h-3 w-3" /> Delete mockup
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


/**
 * Reusable picker for a hero photo or a logo:
 *   - "Upload" file input
 *   - Optional "AI Generate" (Nano Banana) — only enabled when
 *     allowAiGenerate is true (used for hero, not logo)
 *   - "Paste URL" input for any external image URL
 *   - Thumbnail preview + Remove button
 */
function ImagePicker({ label, value, onChange, allowAiGenerate, allowStockSearch, aiPromptFields, testidPrefix, compact }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [stockOpen, setStockOpen] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const r = await uploadMockupAsset(file);
      onChange(r.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleAiGenerate = async () => {
    const bn = aiPromptFields?.business_name?.trim();
    if (!bn) {
      toast.error("Enter the Business Name first — Nano Banana needs it to draw the right photo");
      return;
    }
    setGenerating(true);
    try {
      const r = await generateMockupHeroImage(aiPromptFields);
      onChange(r.data.url);
      toast.success("Hero image generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const applyUrl = () => {
    if (!urlInput.trim()) return;
    onChange(urlInput.trim());
    setUrlInput("");
    toast.success(`${label} set from URL`);
  };

  const previewSrc = value ? mockupAssetUrl(value) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className={compact ? "text-[11px] text-muted-foreground" : "text-xs text-muted-foreground"}>
          {label}
        </Label>
        {value && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700"
            onClick={() => onChange("")}
            data-testid={`${testidPrefix}-remove`}
          >
            <XIcon className="h-3 w-3 mr-0.5" /> Remove
          </Button>
        )}
      </div>

      {previewSrc && (
        <div className="rounded-md border overflow-hidden bg-white">
          <img
            src={previewSrc}
            alt={label}
            className={compact ? "w-full max-h-24 object-contain bg-zinc-100" : "w-full max-h-40 object-contain bg-zinc-100"}
            data-testid={`${testidPrefix}-preview`}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={`${testidPrefix}-file`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-[11px]"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testidPrefix}-upload-btn`}
        >
          {uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <ImagePlus className="mr-1 h-3 w-3" />}
          Upload
        </Button>
        {allowAiGenerate && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px] border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
            onClick={handleAiGenerate}
            disabled={generating}
            data-testid={`${testidPrefix}-ai-btn`}
          >
            {generating ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1 h-3 w-3" />}
            AI Generate
          </Button>
        )}
        {allowStockSearch && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => setStockOpen(true)}
            data-testid={`${testidPrefix}-stock-btn`}
          >
            <Search className="mr-1 h-3 w-3" /> Stock Search
          </Button>
        )}
      </div>
      <div className="flex gap-1.5">
        <Input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="or paste image URL"
          className="h-7 text-[11px]"
          data-testid={`${testidPrefix}-url-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-[11px] shrink-0"
          onClick={applyUrl}
          disabled={!urlInput.trim()}
          data-testid={`${testidPrefix}-url-apply`}
        >
          Use
        </Button>
      </div>

      {stockOpen && (
        <StockSearchDialog
          open={stockOpen}
          onClose={() => setStockOpen(false)}
          onPick={(url) => { onChange(url); setStockOpen(false); }}
          initialQuery={aiPromptFields?.industry || aiPromptFields?.business_name || ""}
          testidPrefix={testidPrefix}
        />
      )}
    </div>
  );
}



/**
 * Modal grid of Unsplash search results. Rep types a keyword, clicks a
 * thumbnail → we import via /api/mockups/import-stock (which fires the
 * Unsplash download-tracking endpoint under the hood), then return the
 * `/api/mockup-assets/{id}` URL back to the caller.
 */
function StockSearchDialog({ open, onClose, onPick, initialQuery, testidPrefix }) {
  const [q, setQ] = useState(initialQuery || "");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(null);
  const [importing, setImporting] = useState(null); // photo id being imported
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    try {
      const r = await stockPhotoSearch(term, 12, 1);
      setResults(r.data.results || []);
      setTotal(r.data.total || 0);
      if ((r.data.results || []).length === 0) {
        toast.info("No matches — try a different keyword");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Stock search failed");
    } finally {
      setLoading(false);
    }
  };

  const pick = async (photo) => {
    setImporting(photo.id);
    try {
      const r = await importStockPhoto({
        photo_id: photo.id,
        download_url: photo.preview_url || photo.full_url,
        download_tracking_url: photo.download_tracking_url,
        photographer_name: photo.photographer_name,
        photographer_url: photo.photographer_url,
        description: photo.description,
      });
      toast.success(`Imported photo by ${photo.photographer_name}`);
      onPick(r.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid={`${testidPrefix}-stock-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Search className="h-4 w-4 text-emerald-600" /> Stock Photo Search · Unsplash
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={runSearch} className="flex gap-2">
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. pole barn, dentist office, restaurant interior…"
            data-testid={`${testidPrefix}-stock-query`}
          />
          <Button type="submit" disabled={loading || !q.trim()} data-testid={`${testidPrefix}-stock-run`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1 hidden sm:inline">Search</span>
          </Button>
        </form>

        <div className="max-h-[520px] overflow-y-auto">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : results.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              <ImageIcon className="h-10 w-10 mx-auto opacity-30 mb-2" />
              {total === null ? "Search for a photo above." : "No results — try another keyword."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pick(p)}
                  disabled={!!importing}
                  className="group relative rounded-md overflow-hidden border hover:ring-2 hover:ring-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed text-left"
                  data-testid={`${testidPrefix}-stock-photo-${p.id}`}
                  style={{ backgroundColor: p.color || "#eee" }}
                >
                  {/* Small thumbnail — Unsplash CDN handles resizing */}
                  <img
                    src={p.thumb_url}
                    alt={p.description || "stock"}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white p-1.5 text-[10px]">
                    <span className="truncate block">Photo · {p.photographer_name}</span>
                  </div>
                  {importing === p.id && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground border-t pt-2">
          Photos via Unsplash — all photographers automatically credited on the mockup.
          {total !== null && total > results.length && (
            <span className="ml-2">Showing {results.length} of {total.toLocaleString()} matches — refine your keyword to narrow down.</span>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Tiny searchable client-picker used on the New Mockup form and the Detail
 * edit panel. Filters the caller-supplied client list client-side so it
 * works even when the user's list is 100s of records.
 */
function ClientLinkPicker({ clients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const active = clients.find((c) => c.id === value);

  const filtered = clients
    .filter((c) => {
      if (!q.trim()) return true;
      const hay = `${c.company || ""} ${c.name || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(q.trim().toLowerCase());
    })
    .slice(0, 30);

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">Link to a Client (optional)</Label>
      {active ? (
        <div className="flex items-center justify-between rounded-md border bg-indigo-50/50 border-indigo-200 px-2.5 py-1.5">
          <span className="inline-flex items-center gap-1.5 text-xs text-indigo-900">
            <UserCircle className="h-3.5 w-3.5" />
            <b>{active.company || active.name || "Client"}</b>
            {active.name && active.company && <span className="text-indigo-700/70">· {active.name}</span>}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700"
            onClick={() => onChange("", "", "")}
            data-testid="mockup-client-unlink"
          >
            <XIcon className="h-3 w-3 mr-0.5" /> Unlink
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[11px]"
          onClick={() => setOpen(true)}
          data-testid="mockup-client-pick"
        >
          <UserCircle className="mr-1 h-3.5 w-3.5" /> Choose a Client
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="mockup-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <UserCircle className="h-4 w-4" /> Link this mockup to a Client
            </DialogTitle>
          </DialogHeader>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by company, name, or email…"
            autoFocus
            data-testid="mockup-client-search"
          />
          <div className="max-h-[380px] overflow-y-auto space-y-1">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">No matching clients.</p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c.id, c.company || "", c.name || "");
                    setOpen(false);
                    setQ("");
                  }}
                  className="w-full text-left px-2.5 py-2 rounded hover:bg-zinc-50 border border-transparent hover:border-zinc-200"
                  data-testid={`mockup-client-option-${c.id}`}
                >
                  <p className="text-sm font-medium">{c.company || c.name || "—"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[c.name && c.name !== c.company ? c.name : null, c.email, c.phone].filter(Boolean).join(" · ")}
                  </p>
                </button>
              ))
            )}
          </div>
          <p className="text-[10px] text-muted-foreground border-t pt-2">
            Linking to a client makes the mockup show up on their row — no more hunting for the last URL you sent.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}



/**
 * A URL field + "Analyze site" button. On analyze we call
 * `/api/mockups/analyze-website`, then bubble suggestions (tagline, primary
 * colour, logo, description note) up to the parent so it can autofill any
 * still-empty fields.
 */
function WebsiteAnalyzerField({ value, onChange, onSuggestions }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null); // last analysis result

  const runAnalyze = async () => {
    const url = (value || "").trim();
    if (!url) {
      toast.error("Enter the prospect's website URL first");
      return;
    }
    setAnalyzing(true);
    try {
      const r = await analyzeProspectWebsite(url);
      setResult(r.data);
      if (!r.data?.ok) {
        toast.error(r.data?.error || "Could not analyze site");
        return;
      }
      // Build a note for Claude with the extracted description so it has
      // richer context than just the URL.
      const noteBits = [];
      if (r.data.title) noteBits.push(`Site title: ${r.data.title}`);
      if (r.data.description) noteBits.push(`Meta description: ${r.data.description}`);
      const description_note = noteBits.length ? noteBits.join(" · ") : "";

      onSuggestions({
        tagline: r.data.description ? r.data.description.split(".")[0].slice(0, 120) : "",
        primary_color: r.data.theme_color || "",
        logo_url: r.data.logo_url || "",
        description_note,
      });

      const filled = [
        r.data.theme_color ? "brand colour" : null,
        r.data.logo_url ? "logo" : null,
        r.data.description ? "tagline" : null,
      ].filter(Boolean);
      toast.success(
        filled.length
          ? `Auto-filled: ${filled.join(", ")}`
          : "Fetched — no obvious brand signals; try filling manually"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analyze failed");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground flex items-center gap-1">
        <Globe className="h-3 w-3" /> Prospect&apos;s Current Website (optional — helps Claude pick colours + copy)
      </Label>
      <div className="flex gap-1.5">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. maecontracting.com or https://…"
          data-testid="mockup-website-url"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runAnalyze(); } }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 text-[11px] shrink-0 border-sky-300 text-sky-700 hover:bg-sky-50"
          onClick={runAnalyze}
          disabled={analyzing || !value?.trim()}
          data-testid="mockup-website-analyze"
        >
          {analyzing ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Globe className="mr-1 h-3 w-3" />}
          Analyze
        </Button>
      </div>
      {result && result.ok && (
        <div className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-start gap-1.5">
          <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" />
          <span className="flex-1">
            <b>{result.title || result.url}</b>
            {result.description && (
              <span className="block text-emerald-800/80 mt-0.5">{result.description.slice(0, 160)}{result.description.length > 160 ? "…" : ""}</span>
            )}
            <span className="block mt-0.5 text-emerald-700/80">
              {result.theme_color ? `• Brand colour: ${result.theme_color} ` : ""}
              {result.logo_url ? "• Logo captured " : ""}
            </span>
          </span>
        </div>
      )}
      {result && !result.ok && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {result.error || "Could not analyze — you can still fill fields manually."}
        </p>
      )}
    </div>
  );
}



/**
 * One-click "Email to Prospect" — captures recipient email + name,
 * optional custom message, and optional booking URL. The backend renders a
 * PNG on the fly and embeds it inline in the email body plus attaches a
 * copy; the shareable preview URL is the primary CTA. Reply-to is the
 * rep's own email so replies flow back to them.
 */
function EmailProspectDialog({ open, onClose, mockup, onSent }) {
  const [toEmail, setToEmail] = useState("");
  const [toName, setToName] = useState(mockup?.client_name || "");
  const [subject, setSubject] = useState(
    mockup?.business_name ? `A homepage concept for ${mockup.business_name}` : ""
  );
  const [message, setMessage] = useState("");
  const [bookingUrl, setBookingUrl] = useState("");
  const [includePng, setIncludePng] = useState(true);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!toEmail.trim() || !/.+@.+\..+/.test(toEmail.trim())) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const r = await emailMockupToProspect(mockup.id, {
        to_email: toEmail.trim(),
        to_name: toName.trim() || null,
        subject: subject.trim() || null,
        message: message.trim() || null,
        booking_url: bookingUrl.trim() || null,
        include_png: includePng,
      });
      if (r.data?.email_sent === false) {
        // Server accepted but did not deliver — surface the reason clearly
        const status = r.data?.email_status;
        const msg = status === "test_mode"
          ? "TEST_MODE is enabled on this environment — the email was NOT sent. Ask your Super Admin to unset TEST_MODE on the deployment."
          : status === "no_resend_key"
          ? "Resend API key is not configured — the email was NOT sent."
          : "The server accepted the request but did not deliver the email.";
        toast.warning(msg, { duration: 12000 });
        onSent?.({ to_email: toEmail.trim(), delivered: false });
      } else {
        toast.success(`Mockup emailed to ${toEmail.trim()}`);
        onSent?.({ to_email: toEmail.trim(), delivered: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mockup-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4 text-fuchsia-600" /> Email this mockup to a prospect
          </DialogTitle>
          <DialogDescription className="text-xs">
            The email includes a live preview link, a PNG snapshot inline, and your signature.
            Replies come back to <b>your inbox</b>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Recipient email *</Label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="owner@theircompany.com"
              required
              autoFocus
              data-testid="mockup-email-to"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Their name (optional)</Label>
            <Input
              value={toName}
              onChange={(e) => setToName(e.target.value)}
              placeholder="e.g. Marissa"
              data-testid="mockup-email-name"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A homepage concept for …"
              data-testid="mockup-email-subject"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Personal note (optional)</Label>
            <Textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Great chatting today — here's the concept I put together based on our call. Would love your thoughts."
              className="resize-y min-h-[110px]"
              data-testid="mockup-email-message"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Booking link (optional)</Label>
            <Input
              value={bookingUrl}
              onChange={(e) => setBookingUrl(e.target.value)}
              placeholder="https://cal.com/…  or Calendly / Google Calendar link"
              data-testid="mockup-email-booking"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">Adds a &quot;📅 Book a quick call&quot; CTA button under your note.</p>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-zinc-700">
            <input
              type="checkbox"
              checked={includePng}
              onChange={(e) => setIncludePng(e.target.checked)}
              data-testid="mockup-email-include-png"
            />
            Include a PNG snapshot inline in the body <span className="text-muted-foreground">(recommended)</span>
          </label>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending || !toEmail.trim()}
              className="bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-700 hover:to-indigo-700"
              data-testid="mockup-email-send"
            >
              {sending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> Send email</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}



/**
 * RefinePanel — chat-style iteration on an existing mockup.
 * The rep types a natural-language change ("make the CTA green", "add a
 * testimonials section", "hero copy is too long — shorten it") and Claude
 * edits the current HTML instead of rebuilding from scratch. Faster and
 * usually converges to what they want in 2–3 turns, same as chatting
 * directly with Claude in Claude Design.
 */
function RefinePanel({ regenerating, history, onRefine }) {
  const [text, setText] = useState("");

  const quicks = [
    "Make the hero CTA button larger and higher contrast",
    "Add a testimonials section between features and CTA",
    "Use their brand color for buttons and section headers",
    "Shorten the hero headline — max 8 words",
    "Add trust badges (BBB, Google, Yelp) below the hero",
  ];

  const submit = () => {
    if (regenerating) return;
    const inst = text.trim();
    if (!inst) { toast.error("Type what you want changed first"); return; }
    onRefine(inst);
    setText("");
  };

  return (
    <Card className="border-purple-200 bg-purple-50/40" data-testid="mockup-refine-panel">
      <CardContent className="pt-5 pb-5 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <h3 className="font-semibold text-sm">Refine (chat with Claude)</h3>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Type the change you want. Claude remembers the current design and just edits it — no need to start over.
        </p>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Move the phone number to the top-right, and make the primary color a warmer orange..."
          rows={3}
          className="text-sm resize-none"
          data-testid="mockup-refine-input"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
        />

        <div className="flex flex-wrap gap-1.5">
          {quicks.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setText((prev) => prev ? `${prev}\n${q}` : q)}
              className="text-[11px] px-2 py-1 rounded-full border bg-white hover:bg-purple-100 text-purple-800 border-purple-200"
              data-testid={`mockup-refine-quick-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
            >
              + {q}
            </button>
          ))}
        </div>

        <div className="flex justify-between items-center gap-2 pt-1">
          <span className="text-[10px] text-muted-foreground">⌘/Ctrl + Enter to send · ~30–60s per turn</span>
          <Button
            size="sm"
            onClick={submit}
            disabled={regenerating || !text.trim()}
            data-testid="mockup-refine-send"
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {regenerating ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Working…</>
            ) : (
              <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Refine</>
            )}
          </Button>
        </div>

        {Array.isArray(history) && history.length > 0 && (
          <details className="text-[11px] text-muted-foreground pt-1">
            <summary className="cursor-pointer select-none">Recent refinements ({history.length})</summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc">
              {history.slice(-5).reverse().map((h, i) => (
                <li key={i} className="line-clamp-2" data-testid={`mockup-refine-history-${i}`}>
                  {h.instruction}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
